import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import api from "@/lib/api";
import { usePresence } from "@/components/claude/ClaudePresence";
import { useAuth } from "@/context/AuthContext";
import { moneyCompact, num, fmtDate, daysLabel, STATUS_LABELS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, XCircle, ArrowRight, CheckCircle2, CheckSquare, Square, Gauge, CalendarClock, Wallet, TrendingUp, Trophy, UserPlus, Download, History, Layers, RefreshCw, BellRing, Clock, RotateCcw, ExternalLink } from "lucide-react";
import { BulkScopeDialog } from "@/components/BulkScopeDialog";
import { BidDecisionDialog } from "@/components/BidDecisionDialog";
import { ConvertLeadDialog } from "@/components/ConvertLeadDialog";
import { ReassignDialog } from "@/components/ReassignDialog";
import { disciplineLabel, scopesFromDoc } from "@/lib/disciplines";
import { PipelineStageDialog } from "@/components/PipelineStageDialog";
import { ArrowRightLeft, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { OutcomeDialog } from "@/components/OutcomeDialog";
import { LeadCaptureDialog, LeadDetailDialog } from "@/components/LeadDialogs";
import ExternalBidDialog from "@/components/ExternalBidDialog";
import { QuickLumpEdit, QuickOutcome } from "@/components/PricedElsewhereCardActions";
import { IntentChip } from "@/pages/Inbox";
import { PhaseChip, NextAction } from "@/components/PhaseChip";
import { EngagementChip, AssigneeAvatar } from "@/components/EngagementChip";
import { TeamAvatars } from "@/components/BidTeam";
import { ProposedReview } from "@/components/ProposedReview";
import { ScopeTag, scopeLabel } from "@/components/ProjectIdentity";
import { StatCard } from "@/components/ui/StatCard";
import { LIFECYCLE } from "@/lib/lifecycle";
import { kindIcon, dueTone } from "@/components/ActivityPanel";
import { DeadlineChip, RoundChip, SignoffChip, RocketFuelLink, DueDates } from "@/components/BidOpsChips";
import { NdaMark } from "@/components/nda/NdaBits";

const STAGES = ["new", "review", "qualified", "estimating", "proposal", "sent", "won", "lost", "no_bid"];
const STAGE_ACCENT = {
  new: "border-t-slate-400", review: "border-t-[#003E87]", qualified: "border-t-[#F4C400]",
  estimating: "border-t-amber-400", proposal: "border-t-indigo-400", sent: "border-t-black",
  won: "border-t-emerald-500", lost: "border-t-rose-400", no_bid: "border-t-slate-300",
};
const BID_COL = { estimating: "estimating", proposal: "proposal", sent: "sent", won: "won", lost: "lost", declined: "no_bid", cancelled: "no_bid" };
const FIT_BAND = { go: "bg-emerald-50 text-emerald-700 border-emerald-200", review: "bg-[#003E87]/10 text-[#003E87] border-[#003E87]/30", pass: "bg-rose-50 text-rose-700 border-rose-200" };
const WIN_TONE = {
  Strong: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Competitive: "bg-[#003E87]/10 text-[#003E87] border-[#003E87]/30",
  "At risk": "bg-amber-50 text-amber-700 border-amber-200",
  "Long shot": "bg-rose-50 text-rose-700 border-rose-200",
};
const EMPTY = {
  company: "", client_id: null, contact_id: null,
  contact_name: "", contact_email: "", contact_phone: "", project_name: "",
  state: "", county: "", capacity_mw: "", source: "", due_date: "", mobilization_date: "",
  notes: "", discipline_scope: "both", discipline: "", additional_disciplines: [], scopes: [],
};
const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");

export default function Pipeline() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const canCapture = ["owner", "admin", "chief_estimator", "business_development"].includes(role);
  const isAdmin = ["owner", "admin"].includes(role);
  const canCancel = ["owner", "admin", "chief_estimator", "business_development"].includes(role);
  const canPrice = ["owner", "admin", "chief_estimator", "estimator"].includes(role);

  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [extOpen, setExtOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [lead, setLead] = useState(null);        // selected lead (detail dialog)
  const [leadEdit, setLeadEdit] = useState(EMPTY);
  const [cancelling, setCancelling] = useState(null); // bid item
  const [cancelReason, setCancelReason] = useState("");
  const [dropping, setDropping] = useState(null); // lead item
  const [dropReason, setDropReason] = useState("");
  const [deleting, setDeleting] = useState(null); // item {kind,id,name}
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkStageOpen, setBulkStageOpen] = useState(false);
  const [bulkScopeOpen, setBulkScopeOpen] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [decisionLead, setDecisionLead] = useState(null);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [convertLeadItem, setConvertLeadItem] = useState(null);
  const [outcomeBid, setOutcomeBid] = useState(null);
  const [convertBid, setConvertBid] = useState(null); // existing bid being priced elsewhere
  const [users, setUsers] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("gf.pipeline.view") || "list");
  const [stageFilter, setStageFilter] = useState("all");
  const [coldOnly, setColdOnly] = useState(false);
  const pickView = (v) => { setView(v); localStorage.setItem("gf.pipeline.view", v); };
  const presence = usePresence();
  const claudeOn = presence?.current?.active ? (presence.current.bid_id || presence.current.lead_id) : null;
  const [winMap, setWinMap] = useState({});
  const [winStats, setWinStats] = useState(null);
  const [nextMap, setNextMap] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncProg, setSyncProg] = useState(null);

  const syncAllEmail = async () => {
    setSyncingAll(true); setSyncProg(null);
    try {
      await api.post("/email/sync-all");
      toast.info("Scanning every project's inbox — you can keep working…");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get("/email/sync-all-status");
          setSyncProg({ done: data.done || 0, total: data.total || 0 });
          if (!data.running) {
            const m = data.last?.logged ?? 0;
            toast.success(`All projects synced — ${m} new email${m === 1 ? "" : "s"} linked`);
            setSyncingAll(false); setSyncProg(null); load();
            return;
          }
        } catch { /* keep polling */ }
        if (tries < 150) setTimeout(poll, 2000);
        else { setSyncingAll(false); setSyncProg(null); load(); }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connect Gmail in the Inbox first");
      setSyncingAll(false); setSyncProg(null);
    }
  };

  const keyOf = (it) => `${it.kind}:${it.id}`;
  const toggleSelect = (it) => {
    const k = keyOf(it);
    setSelected((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  };
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };
  const toggleColumn = (colItems) => {
    setSelected((prev) => {
      const n = new Set(prev);
      const allSel = colItems.length > 0 && colItems.every((it) => n.has(keyOf(it)));
      colItems.forEach((it) => { const k = keyOf(it); allSel ? n.delete(k) : n.add(k); });
      return n;
    });
  };
  const selectAllVisible = () => {
    setSelected((prev) => {
      const keys = shownItems.map(keyOf);
      const allSel = keys.length > 0 && keys.every((k) => prev.has(k));
      return allSel ? new Set() : new Set(keys);
    });
  };
  const bulkDelete = async () => {
    const payload = [...selected].map((k) => { const [kind, id] = k.split(":"); return { kind, id }; });
    try {
      const res = await api.post("/pipeline/bulk-delete", { items: payload });
      const d = res.data.deleted; const total = d.leads + d.bids;
      toast.success(`Deleted ${total} item${total === 1 ? "" : "s"}`);
      setBulkOpen(false); exitSelect(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const bulkAssign = async (uid) => {
    const payload = [...selected].map((k) => { const [kind, id] = k.split(":"); return { kind, id }; });
    try {
      const res = await api.post("/pipeline/bulk", { action: "assign", items: payload, assignee_id: uid });
      toast.success(`Reassigned ${res.data.updated} item${res.data.updated === 1 ? "" : "s"}`);
      setBulkAssignOpen(false); exitSelect(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reassign"); }
  };
  const quickScope = (it) => { setSelected(new Set([keyOf(it)])); setBulkScopeOpen(true); };
  const reopenOne = async (it) => {
    try {
      await api.post("/pipeline/bulk", { action: "reopen", items: [{ kind: it.kind, id: it.id }] });
      toast.success(`Reopened “${it.name}” back to the pipeline`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reopen"); }
  };
  const bulkReopen = async () => {
    const items = [...selected].map((k) => { const [kind, id] = k.split(":"); return { kind, id }; });
    try {
      const res = await api.post("/pipeline/bulk", { action: "reopen", items });
      toast.success(`Reopened ${res.data.updated} item${res.data.updated === 1 ? "" : "s"}`);
      exitSelect(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reopen"); }
  };
  const bulkStage = async (stage) => {
    const items = [...selected].map((k) => { const [kind, id] = k.split(":"); return { kind, id }; });
    try {
      const res = await api.post("/pipeline/bulk", { action: "stage", items, stage });
      const msg = `Moved ${res.data.updated} item${res.data.updated === 1 ? "" : "s"}` + (res.data.skipped ? ` · ${res.data.skipped} skipped` : "");
      toast.success(msg);
      setBulkStageOpen(false); exitSelect(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change stage"); }
  };
  const bulkScope = async (payload) => {
    try {
      const res = await api.post("/pipeline/bulk", { action: "scope", ...payload });
      toast.success(`Updated ${res.data.updated} project${res.data.updated === 1 ? "" : "s"} · ${res.data.renamed} renamed · ${res.data.numbered} numbered`);
      setBulkScopeOpen(false); exitSelect(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update scope"); }
  };
  const bulkExport = async () => {
    const bidIds = [...selected].filter((k) => k.startsWith("bid:")).map((k) => k.split(":")[1]);
    if (!bidIds.length) { toast.error("Select at least one estimate/bid — leads can't be exported."); return; }
    try {
      const res = await api.post("/bids/export", { bid_ids: bidIds }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "pipeline-export.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const loadFollowups = () => api.get("/crm/followups").then((r) => setNextMap(r.data?.next_map || {})).catch(() => {});
  const load = async () => {
    const res = await api.get("/pipeline", { params: includeArchived ? { include_all: true } : {} });
    setItems(res.data);
    loadFollowups();
    api.get("/pipeline/win-stats").then((r) => setWinStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [includeArchived]);
  useEffect(() => { if (params.get("new") === "1") setOpen(true); }, [params]);
  const { leadId: routeLeadId } = useParams();
  useEffect(() => { const id = routeLeadId || params.get("lead"); if (id) openLead(id); }, [routeLeadId, params]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/insights/win-trend").then((r) => {
      const m = {};
      (r.data?.points || []).forEach((p) => { m[p.bid_id] = { score: p.score, label: p.label, vs: p.vs_median_pct }; });
      setWinMap(m);
    }).catch(() => {});
  }, [items]);

  const stageOf = (it) => (it.kind === "lead" ? it.status : (BID_COL[it.status] || "estimating"));
  const pfIds = new Set(items.filter((it) => it.kind === "portfolio").map((it) => it.id));
  const folded = items.filter((it) => !(it.kind === "bid" && it.portfolio_id && pfIds.has(it.portfolio_id)));
  const ownerItems = ownerFilter === "all" ? folded
    : folded.filter((it) => (it.assignee?.user_id || null) === ownerFilter);
  const outcomeCounts = { Won: 0, Lost: 0, Abandoned: 0 };
  ownerItems.forEach((it) => { if (it.outcome_word in outcomeCounts) outcomeCounts[it.outcome_word] += 1; });
  const shownItems = outcomeFilter === "all" ? ownerItems : ownerItems.filter((it) => it.outcome_word === outcomeFilter);
  const grouped = STAGES.map((s) => ({ stage: s, items: shownItems.filter((it) => stageOf(it) === s) }))
    .filter((col) => outcomeFilter === "all" || col.items.length > 0);
  const allVisibleSelected = shownItems.length > 0 && shownItems.every((it) => selected.has(keyOf(it)));

  // Pipeline forecast summary — history-weighted where we have a win-probability signal.
  // Forecast KPIs reflect the LIVE pipeline only; imported historical wins are shown in the
  // Won lane for visibility but excluded here so they don't skew win-rate/forecast.
  const STAGE_PROB = { new: 0.05, review: 0.1, qualified: 0.2, estimating: 0.35, proposal: 0.5, sent: 0.6 };
  const OPEN_STAGES = ["estimating", "proposal", "sent"];
  const liveItems = shownItems.filter((it) => !it.is_historical);
  const probOf = (it) => {
    const st = stageOf(it);
    if (it.kind === "bid" && OPEN_STAGES.includes(st) && winMap[it.id]?.score != null) return winMap[it.id].score / 100;
    return STAGE_PROB[st] || 0;
  };
  const weighted = liveItems.reduce((s, it) => s + (it.stored_total || 0) * probOf(it), 0);
  const openValue = liveItems.reduce((s, it) => (["estimating", "proposal", "sent"].includes(stageOf(it)) ? s + (it.stored_total || 0) : s), 0);
  const wonC = winStats ? winStats.won : liveItems.filter((it) => stageOf(it) === "won").length;
  const lostC = winStats ? winStats.lost : liveItems.filter((it) => ["lost", "no_bid"].includes(stageOf(it))).length;
  const winRate = winStats ? winStats.win_rate : (wonC + lostC > 0 ? Math.round((100 * wonC) / (wonC + lostC)) : null);
  const activeCount = liveItems.filter((it) => ["new", "review", "qualified", "estimating", "proposal", "sent"].includes(stageOf(it))).length;

  const captureLead = async () => {
    if (!form.project_name.trim() && !form.company.trim()) {
      toast.error("Add at least a project name or a company."); return;
    }
    if (!form.scopes || form.scopes.length === 0) { toast.error("Add at least one scope."); return; }
    setSaving(true);
    try {
      await api.post("/leads", {
        ...form,
        capacity_mw: form.capacity_mw || null,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        mobilization_date: form.mobilization_date ? new Date(form.mobilization_date).toISOString() : null,
      });
      toast.success("Lead captured");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save lead"); }
    finally { setSaving(false); }
  };

  const openLead = async (id) => {
    try {
      const res = await api.get(`/leads/${id}`);
      setLead(res.data);
      setLeadEdit({
        company: res.data.company || "", contact_name: res.data.contact_name || "",
        contact_email: res.data.contact_email || "", contact_phone: res.data.contact_phone || "",
        project_name: res.data.project_name || "", state: res.data.state || "",
        county: res.data.county || "",
        capacity_mw: res.data.capacity_mw ?? "", source: res.data.source || "",
        due_date: toDateInput(res.data.due_date),
        mobilization_date: toDateInput(res.data.mobilization_date), notes: res.data.notes || "",
        discipline_scope: res.data.discipline_scope || "both",
        scopes: scopesFromDoc(res.data),
      });
    } catch { toast.error("Could not open lead"); }
  };

  const saveLead = async (silent = false) => {
    try {
      const res = await api.put(`/leads/${lead.lead_id}`, {
        ...leadEdit,
        due_date: leadEdit.due_date ? new Date(leadEdit.due_date).toISOString() : null,
        mobilization_date: leadEdit.mobilization_date ? new Date(leadEdit.mobilization_date).toISOString() : null,
        capacity_mw: leadEdit.capacity_mw || null,
      });
      setLead(res.data); if (!silent) toast.success("Saved"); load();
      return res.data;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); return null; }
  };

  const openDecision = (lead) => {
    setLead(null);
    setDecisionLead(lead);
    setDecisionOpen(true);
  };

  const convertLead = () => {
    setConvertLeadItem(lead);
    setLead(null);
  };

  const confirmDrop = async () => {
    if (!dropReason.trim()) { toast.error("A reason is required."); return; }
    try {
      await api.post(`/leads/${dropping.id}/drop`, { reason: dropReason.trim() });
      toast.success("Lead dropped"); setDropping(null); setDropReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not drop"); }
  };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) { toast.error("A reason is required."); return; }
    try {
      await api.post(`/bids/${cancelling.id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Pursuit cancelled"); setCancelling(null); setCancelReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(deleting.kind === "lead" ? `/leads/${deleting.id}` : `/bids/${deleting.id}`);
      toast.success("Deleted"); setDeleting(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const gaps = lead ? [
    !leadEdit.capacity_mw && "capacity",
    !leadEdit.state?.trim() && "state",
    !leadEdit.due_date && "target bid date",
  ].filter(Boolean) : [];

  return (
    <div className="px-6 sm:px-8 py-8 h-full flex flex-col">
      <ProposedReview onChange={load} />
      <div className="flex items-start justify-between gap-4 mb-6 shrink-0 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold tracking-tight text-black">Pipeline</h1>
          <p className="text-sm text-slate-500 mt-0.5">Every pursuit, first contact to won or lost. Leads become estimates on conversion.</p>
        </div>
        {selectMode ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">{selected.size} selected</span>
            <Button variant="outline" data-testid="pipeline-select-all-visible" onClick={selectAllVisible}>{allVisibleSelected ? "Deselect all" : "Select all"}</Button>
            <Button variant="outline" onClick={() => setSelected(new Set())} disabled={selected.size === 0}>Clear</Button>
            <Button variant="outline" data-testid="pipeline-include-archived-sel" onClick={() => setIncludeArchived((v) => !v)} className={includeArchived ? "border-[#003E87] text-[#003E87] bg-[#003E87]/5" : ""}>
              <History className="w-4 h-4 mr-1.5" /> {includeArchived ? "All shown" : "Historical"}
            </Button>
            <Button variant="outline" data-testid="pipeline-bulk-assign-btn" onClick={() => setBulkAssignOpen(true)} disabled={selected.size === 0}>
              <UserPlus className="w-4 h-4 mr-1.5" /> Assign
            </Button>
            <Button variant="outline" data-testid="pipeline-bulk-scope-btn" onClick={() => setBulkScopeOpen(true)} disabled={selected.size === 0}>
              <Layers className="w-4 h-4 mr-1.5" /> Set scope
            </Button>
            <Button variant="outline" data-testid="pipeline-bulk-stage-btn" onClick={() => setBulkStageOpen(true)} disabled={selected.size === 0}>
              <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Stage
            </Button>
            <Button variant="outline" data-testid="pipeline-bulk-reopen-btn" onClick={bulkReopen} disabled={selected.size === 0}>
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reopen
            </Button>
            <Button variant="outline" data-testid="pipeline-bulk-export-btn" onClick={bulkExport} disabled={selected.size === 0}>
              <Download className="w-4 h-4 mr-1.5" /> Export
            </Button>
            <Button data-testid="pipeline-bulk-delete-btn" onClick={() => setBulkOpen(true)} disabled={selected.size === 0} className="bg-rose-600 hover:bg-rose-700 text-white">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete ({selected.size})
            </Button>
            <Button variant="ghost" data-testid="pipeline-select-done" onClick={exitSelect}>Done</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="h-9 w-40 text-sm" data-testid="pipeline-owner-filter">
                <SelectValue placeholder="All owners" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                {user?.user_id && <SelectItem value={user.user_id}>Mine</SelectItem>}
                {users.filter((u) => u.user_id !== user?.user_id).map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" data-testid="pipeline-include-archived" onClick={() => setIncludeArchived((v) => !v)} className={includeArchived ? "border-[#003E87] text-[#003E87] bg-[#003E87]/5" : ""}>
              <History className="w-4 h-4 mr-1.5" /> {includeArchived ? "Showing all" : "Include historical"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="pipeline-more-btn"><MoreHorizontal className="w-4 h-4 mr-1.5" /> More</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem data-testid="pipeline-sync-all-email" disabled={syncingAll} onClick={syncAllEmail}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncingAll ? "animate-spin" : ""}`} />
                  {syncingAll ? (syncProg?.total ? `Syncing ${syncProg.done}/${syncProg.total}…` : "Syncing…") : "Sync everyone's email"}
                </DropdownMenuItem>
                {canCapture && <DropdownMenuItem data-testid="pipeline-log-external-btn" onClick={() => setExtOpen(true)}><Wallet className="w-4 h-4 mr-2" /> Log an external bid</DropdownMenuItem>}
                {isAdmin && <DropdownMenuItem data-testid="pipeline-select-btn" onClick={() => setSelectMode(true)}><CheckSquare className="w-4 h-4 mr-2" /> Select several</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
            {canCapture && (
              <Button data-testid="pipeline-new-lead-btn" onClick={() => setOpen(true)} className="bg-[#003E87] hover:bg-[#002B5E] text-white font-semibold">
                <Plus className="w-4 h-4 mr-1.5" /> New lead
              </Button>
            )}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5 shrink-0 stagger-children" data-testid="pipeline-forecast-bar">
          <StatCard compact testid="pipeline-kpi-weighted" icon={TrendingUp} accent="#10B981" label="Weighted forecast" value={moneyCompact(weighted)} sub="history-weighted" />
          <StatCard compact testid="pipeline-kpi-openvalue" icon={Wallet} accent="#003E87" label="Open value" value={moneyCompact(openValue)} sub="estimating → sent" />
          <StatCard compact testid="pipeline-kpi-winrate" icon={Trophy} accent="#F4C400" label="Win rate" value={winRate != null ? `${winRate}%` : "—"} sub={`${wonC} won · ${lostC} lost`} />
          <StatCard compact testid="pipeline-kpi-active" icon={Gauge} accent="#6366F1" label="Active pursuits" value={activeCount} sub={`${items.length} total`} />
        </div>
      )}

      {/* End-to-end journey legend */}
      <div className="flex items-center gap-1.5 mb-3 shrink-0 flex-wrap" data-testid="pipeline-outcome-chips">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mr-1">Closed work</span>
        {[["all", "Everything"], ["Won", "Won"], ["Lost", "Lost"], ["Abandoned", "Abandoned"]].map(([k, l]) => {
          const on = outcomeFilter === k;
          const tone = k === "Won" ? "border-emerald-300 text-emerald-800 bg-emerald-50" : k === "Lost" ? "border-rose-300 text-rose-800 bg-rose-50" : k === "Abandoned" ? "border-slate-300 text-slate-700 bg-slate-100" : "border-[#003E87] text-[#003E87] bg-[#003E87]/5";
          return (
            <button key={k} data-testid={`pipeline-outcome-chip-${k.toLowerCase()}`} onClick={() => setOutcomeFilter(on && k !== "all" ? "all" : k)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? tone : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"}`}>
              {l}{k !== "all" && <span className="font-mono text-[10px] opacity-70">{outcomeCounts[k]}</span>}
            </button>
          );
        })}
        {outcomeFilter !== "all" && <span className="text-xs text-slate-400 ml-1" data-testid="pipeline-outcome-filter-note">showing {shownItems.length} {outcomeFilter.toLowerCase()} · lanes without a match are hidden</span>}
      </div>
      <div className="hidden md:flex items-center gap-1 mb-5 shrink-0 rounded-lg border border-slate-200 bg-white px-4 py-2.5 overflow-x-auto">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mr-2 shrink-0">Lifecycle</span>
        {LIFECYCLE.map((s, i) => (
          <React.Fragment key={s.key}>
            <span className="text-xs font-medium text-slate-600 whitespace-nowrap">{s.label}</span>
            {i < LIFECYCLE.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300 shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      <div className="flex items-center gap-1.5 mb-3 shrink-0 flex-wrap" data-testid="pipeline-view-toggle">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50 mr-2">
          {[["list", "List"], ["board", "Board"]].map(([k, l]) => (
            <button key={k} data-testid={`pipeline-view-${k}`} onClick={() => pickView(k)} className={`px-3 py-1 text-xs rounded-md ${view === k ? "bg-white shadow-sm font-semibold text-[#003E87]" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        {view === "list" && (<>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mr-1">Stage</span>
          {[["all", "All"], ...STAGES.map((st) => [st, STATUS_LABELS[st]])].map(([k, l]) => (
            <button key={k} data-testid={`pipeline-stage-chip-${k}`} onClick={() => setStageFilter(k)}
              className={`rounded-full border px-2.5 py-0.5 text-xs ${stageFilter === k ? "border-[#003E87] text-[#003E87] bg-[#003E87]/5 font-medium" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{l}</button>
          ))}
          <button data-testid="pipeline-cold-filter" onClick={() => setColdOnly(!coldOnly)}
            className={`rounded-full border px-2.5 py-0.5 text-xs ${coldOnly ? "border-sky-400 text-sky-800 bg-sky-50 font-medium" : "border-slate-200 bg-white text-slate-600"}`}>Went cold</button>
        </>)}
      </div>
      {view === "list" && (
        <div className="flex-1 min-h-0 overflow-y-auto rounded-lg border border-slate-200 bg-white" data-testid="pipeline-list">
          <table className="w-full table-fixed text-sm">
            <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 w-[26%]">Name</th><th className="text-left px-3 py-2 w-[16%]">Client</th><th className="text-left px-3 py-2 w-[12%]">Scope</th>
                <th className="text-left px-3 py-2 w-[10%]">Stage</th><th className="text-right px-3 py-2 w-[10%]">Price</th><th className="text-left px-3 py-2 w-[10%]">Owner</th>
                <th className="text-left px-3 py-2 w-[8%]">Due</th><th className="text-left px-3 py-2 w-[8%]">Next step</th>
              </tr>
            </thead>
            <tbody>
              {shownItems.filter((it) => (stageFilter === "all" || stageOf(it) === stageFilter) && (!coldOnly || it.cold)).map((it) => (
                <tr key={keyOf(it)} data-testid={`pipeline-row-${it.id}`} onClick={() => (it.kind === "lead" ? openLead(it.id) : it.kind === "portfolio" ? navigate(`/portfolios?pf=${it.id}`) : navigate(`/bids/${it.id}`))}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2 truncate font-medium text-slate-900">
                    {claudeOn === it.id && <span className="relative inline-flex h-2 w-2 mr-1.5 rounded-full align-middle" style={{ background: "#D97757" }} data-testid={`claude-here-${it.id}`}><span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: "#D97757" }} /></span>}
                    {it.name}
                  </td>
                  <td className="px-3 py-2 truncate text-slate-600">{it.client || it.subtitle || "—"}</td>
                  <td className="px-3 py-2 truncate text-slate-600">{it.scope ? scopeLabel(it.scope) : it.discipline ? disciplineLabel(it.discipline) : "—"}</td>
                  <td className="px-3 py-2 truncate">
                    <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${it.cold ? "bg-sky-50 text-sky-800" : it.signoff_waiting ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                      {it.cold ? "Went cold" : it.signoff_waiting ? "Sign-off waiting" : STATUS_LABELS[stageOf(it)] || stageOf(it)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-slate-800">{it.stored_total ? moneyCompact(it.stored_total) : "—"}</td>
                  <td className="px-3 py-2 truncate text-slate-600" data-testid={`row-team-${it.id}`}>{it.assignee?.name || "—"}{it.team?.length ? <span className="text-slate-400"> +{it.team.length}</span> : null}</td>
                  <td className="px-3 py-2 truncate text-slate-600">{it.due_date ? fmtDate(it.due_date) : "—"}</td>
                  <td className="px-3 py-2 truncate text-slate-600">{it.next_step || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className={`flex-1 min-h-0 overflow-x-auto overflow-y-hidden pb-4 ${view === "list" ? "hidden" : ""}`}>
        <div className="flex gap-3 h-full min-w-max">
          {grouped.map((col) => (
            <div key={col.stage} data-testid={`crm-stage-column-${col.stage}`}
              className="w-72 shrink-0 flex flex-col bg-slate-50/60 rounded-lg border border-slate-200">
              <div className={`flex items-center justify-between px-3 py-2.5 border-t-2 rounded-t-lg ${STAGE_ACCENT[col.stage]}`}>
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{STATUS_LABELS[col.stage]}</span>
                <div className="flex items-center gap-2">
                  {selectMode && col.items.length > 0 && (
                    <button data-testid={`select-all-${col.stage}`} onClick={() => toggleColumn(col.items)} className="text-slate-400 hover:text-[#003E87]" title="Select all in this column">
                      {col.items.every((it) => selected.has(keyOf(it))) ? <CheckSquare className="w-3.5 h-3.5 text-[#003E87]" /> : <Square className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  <span className="text-xs text-slate-400 font-mono bg-white rounded-full px-2 py-0.5 border border-slate-200">{col.items.length}</span>
                </div>
              </div>
              {OPEN_STAGES.includes(col.stage) && (() => {
                const w = col.items.reduce((s, it) => s + (it.stored_total || 0) * probOf(it), 0);
                return w > 0 ? (
                  <div className="px-3 pb-1.5 -mt-1 text-[10px] font-mono text-emerald-700" data-testid={`stage-weighted-${col.stage}`}>
                    wtd {moneyCompact(w)}
                  </div>
                ) : null;
              })()}
              <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2">
                {col.items.map((it) => (
                  <Card
                    key={`${it.kind}-${it.id}`}
                    data-testid={`pipeline-card-${it.id}`}
                    className={`group relative p-3 cursor-pointer hover:border-[#003E87]/40 hover:shadow-sm transition bg-white ${selectMode ? "pl-8" : ""} ${selected.has(keyOf(it)) ? "ring-2 ring-[#003E87] border-[#003E87]" : ""}`}
                    onClick={() => (selectMode ? toggleSelect(it) : it.kind === "lead" ? openLead(it.id) : it.kind === "portfolio" ? navigate(`/portfolios?pf=${it.id}`) : navigate(`/bids/${it.id}`))}
                  >
                    {it.kind === "portfolio" && (
                      <div data-testid={`portfolio-card-${it.id}`} className="mb-1.5 rounded-md border border-[#003E87]/20 bg-[#003E87]/5 px-2 py-1.5 text-[11px] text-slate-700">
                        <div className="font-semibold text-[#003E87] text-[10px] uppercase tracking-wide">Portfolio · {it.sites.length} sites{it.scenario ? ` · sent as ${it.scenario}` : ""}</div>
                        {it.sites.map((m) => <div key={m.id} className="flex justify-between gap-2" data-testid={`portfolio-card-site-${m.id}`}><span className="truncate">{m.name}</span><span className="font-mono">{m.stored_total ? moneyCompact(m.stored_total) : "—"}</span></div>)}
                        <div className="flex justify-between gap-2 border-t border-[#003E87]/15 mt-1 pt-1 font-semibold"><span>One award</span><span className="font-mono">{it.stored_total ? moneyCompact(it.stored_total) : "—"}</span></div>
                      </div>
                    )}
                    {selectMode && (
                      <div className="absolute top-2.5 left-2.5" data-testid={`pipeline-select-${it.id}`}>
                        {selected.has(keyOf(it)) ? <CheckSquare className="w-4 h-4 text-[#003E87]" /> : <Square className="w-4 h-4 text-slate-300" />}
                      </div>
                    )}
                    {it.kind === "lead" && (
                      <span className="inline-block mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#003E87] bg-[#003E87]/10 rounded px-1.5 py-0.5">Lead</span>
                    )}
                    {false && it.kind === "lead" && it.fit_score != null && (
                      <span data-testid={`fit-badge-${it.id}`} className={`inline-flex items-center gap-1 mb-1 ml-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border ${FIT_BAND[it.fit_band] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                        <Gauge className="w-2.5 h-2.5" /> Fit {it.fit_score}
                      </span>
                    )}
                    {it.kind === "bid" && it.is_historical && (
                      <span data-testid={`historical-badge-${it.id}`} className="inline-block mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">Historical</span>
                    )}
                    {it.kind === "bid" && it.external_priced && (
                      <span data-testid={`external-badge-${it.id}`} className="inline-block mb-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">Priced elsewhere</span>
                    )}
                    {false && it.kind === "bid" && it.approved_after_send && (
                      <span data-testid={`after-send-badge-${it.id}`} title={`Approved after send by ${it.approved_after_send.by_name || ""} · ${it.approved_after_send.key || ""}`} className="inline-block mb-1 ml-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Approved after send</span>
                    )}
                    {false && it.kind === "bid" && it.cold && (
                      <span data-testid={`cold-badge-${it.id}`} className="inline-block mb-1 ml-1 text-[10px] font-semibold uppercase tracking-wide text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">Went cold</span>
                    )}
                    {false && it.kind === "bid" && it.bundle_id && (() => {
                      const mates = items.filter((o) => o.kind === "bid" && o.bundle_id === it.bundle_id);
                      const ca = it.combined_award || {};
                      return (
                        <div data-testid={`bundle-${it.id}`} className="mb-1.5 rounded-md border border-[#003E87]/20 bg-[#003E87]/5 px-2 py-1.5 text-[11px] text-slate-700">
                          <div className="font-semibold text-[#003E87] text-[10px] uppercase tracking-wide">One project · {mates.length} scopes</div>
                          {mates.map((m) => <div key={m.id} className="flex justify-between gap-2"><span className="truncate">{m.discipline || m.name}</span><span className="font-mono">{m.stored_total ? moneyCompact(m.stored_total) : "—"}</span></div>)}
                          {(ca.combined_total || ca.credit) && <div className="flex justify-between gap-2 border-t border-[#003E87]/15 mt-1 pt-1 font-semibold"><span>Combined{ca.credit ? ` (credit ${moneyCompact(ca.credit)})` : ""}</span><span className="font-mono">{ca.combined_total ? moneyCompact(ca.combined_total) : "—"}</span></div>}
                        </div>
                      );
                    })()}
                    {it.kind === "bid" && it.imported && !it.is_historical && !it.external_priced && (
                      <span data-testid={`imported-badge-${it.id}`} className="inline-block mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#8a6d00] bg-[#F4C400]/20 rounded px-1.5 py-0.5">Imported</span>
                    )}
                    {it.kind === "lead" && it.source === "Email RFQ" && (
                      <span data-testid={`rfq-badge-${it.id}`} className="inline-block mb-1 ml-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">New RFQ</span>
                    )}
                    <div className="text-sm font-medium text-black line-clamp-2 pr-6">{it.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{it.subtitle || (it.kind === "lead" ? "No company" : "No client")}</div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {it.nda_state && it.nda_state !== "not_required" && it.nda_state !== "waived" && <NdaMark state={it.nda_state} testid={`card-nda-${it.id}`} />}
                      {it.project_number && (
                        <span className="text-[10px] font-mono font-semibold text-[#003E87] bg-[#003E87]/10 rounded px-1.5 py-0.5" data-testid={`card-projnum-${it.id}`}>{it.bid_number || it.project_number}</span>
                      )}
                      {it.kind === "bid" && (it.project_scopes || []).length > 0 && (
                        <span className="inline-flex items-center gap-0.5" data-testid={`card-scopes-${it.id}`} title={`Scopes on this project: ${(it.project_scopes || []).join(", ")}`}>
                          {(it.project_scopes || []).map((l) => <ScopeTag key={l} letter={l} active={l === it.scope_letter} />)}
                        </span>
                      )}
                      {it.tidy?.refuse?.length > 0 && (
                        <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200" data-testid={`card-tidy-${it.id}`} title={it.tidy.refuse.join(" ")}>to tidy</span>
                      )}
                      {it.discipline ? (
                        <button type="button" data-testid={`card-discipline-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); if (canCapture) quickScope(it); }}
                          className="text-[10px] rounded px-1.5 py-0.5 border border-slate-200 text-slate-600 hover:border-[#003E87] hover:text-[#003E87] transition">
                          {disciplineLabel(it.discipline)}
                        </button>
                      ) : canCapture ? (
                        <button type="button" data-testid={`card-set-scope-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); quickScope(it); }}
                          className="text-[10px] rounded px-1.5 py-0.5 border border-dashed border-amber-300 text-amber-600 hover:bg-amber-50 transition">
                          + Set scope
                        </button>
                      ) : null}
                    </div>
                    {it.kind === "bid" && it.owed && (
                      <div className="mt-1.5" data-testid={`card-owe-${it.id}`}>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border ${it.waiting_on === "client" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-[#F4C400]/15 text-[#8a6d00] border-[#F4C400]/40"}`}
                          title={it.owed_reason || (it.waiting_on === "client" ? "Time for a check-in" : "You owe a reply")}>
                          {it.waiting_on === "client" ? <Clock className="w-2.5 h-2.5" /> : <BellRing className="w-2.5 h-2.5" />}
                          {it.waiting_on === "client" ? "Check in" : "You owe a reply"}
                        </span>
                      </div>
                    )}
                    {it.kind === "bid" && (it.deadline_flag || it.signoff_waiting?.length > 0 || it.revision > 0 || it.rocketfuel_url || (it.internal_due_date && ["estimating", "proposal"].includes(it.status))) && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`card-ops-${it.id}`}>
                        {["estimating", "proposal"].includes(it.status) && <DueDates due={it.due_date} internal={it.internal_due_date} testid={`card-duedates-${it.id}`} />}
                        <DeadlineChip flag={it.deadline_flag} internalFlag={it.internal_flag} sentOnTime={it.sent_on_time} testid={`card-deadline-${it.id}`} />
                        <SignoffChip names={it.signoff_waiting} testid={`card-signoff-${it.id}`} />
                        {it.revision > 0 && <RoundChip revision={it.revision} sentAt={it.sent_at} testid={`card-round-${it.id}`} />}
                        <RocketFuelLink url={it.rocketfuel_url} label testid={`card-rocketfuel-${it.id}`} />
                      </div>
                    )}
                    {false && it.kind === "bid" && ["estimating", "proposal", "sent"].includes(stageOf(it)) && winMap[it.id]?.score > 0 && (
                      <div className="mt-1.5" data-testid={`win-chip-${it.id}`}>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border ${WIN_TONE[winMap[it.id].label] || "bg-slate-50 text-slate-600 border-slate-200"}`}
                          title={`vs your won-bid median ${winMap[it.id].vs != null ? `(${winMap[it.id].vs > 0 ? "+" : ""}${winMap[it.id].vs}%)` : ""}`}>
                          <TrendingUp className="w-2.5 h-2.5" /> Win {winMap[it.id].score}%
                        </span>
                      </div>
                    )}
                    {(() => {
                      const ns = nextMap[`${it.kind}:${it.id}`];
                      const openPursuit = (it.kind === "lead" && ["new", "review", "qualified"].includes(it.status)) || (it.kind === "bid" && ["estimating", "proposal", "sent"].includes(it.status));
                      if (ns) {
                        const Icon = kindIcon(ns.kind);
                        return (
                          <div className="mt-1.5" data-testid={`nextstep-chip-${it.id}`}>
                            <span className={`inline-flex items-center gap-1 max-w-full text-[10px] font-medium rounded px-1.5 py-0.5 border ${dueTone(ns.days)}`} title={ns.subject}>
                              <Icon className="w-2.5 h-2.5 shrink-0" /><span className="truncate max-w-[120px]">{ns.subject}</span><span className="opacity-70 shrink-0">· {ns.due_at ? daysLabel(ns.days) : "no date"}</span>
                            </span>
                          </div>
                        );
                      }
                      if (openPursuit) {
                        return (
                          <div className="mt-1.5" data-testid={`nextstep-none-${it.id}`}>
                            <span className="inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 border border-dashed border-slate-300 text-slate-400">No next step</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {(it.signal || (it.kind === "bid" && it.latest_intent) || it.engagement) && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`card-chips-${it.id}`}>
                        {it.kind === "bid" && it.latest_intent && <IntentChip intent={it.latest_intent} />}
                        <PhaseChip signal={it.signal} testid={`card-phase-${it.id}`} />
                        <EngagementChip label={it.engagement} replyRate={it.reply_rate_pct} testid={`card-engagement-${it.id}`} />
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-slate-400 font-mono">{num(it.capacity_mw)} MW{it.state ? ` · ${it.state}` : ""}</span>
                      <div className="flex items-center gap-2">
                        {it.kind === "bid" && it.external_priced && canPrice && !selectMode && it.stored_total && !it.is_historical && ["estimating", "proposal", "sent"].includes(it.status) ? (
                          <QuickLumpEdit item={it} onSaved={load} />
                        ) : it.kind === "bid" && it.stored_total ? (
                          <span className="text-xs font-mono font-medium text-slate-700" title={it.standalone_total ? `sites alone ${moneyCompact(it.standalone_total)} · portfolio credit −${moneyCompact(it.portfolio_credit || 0)} · award price ${moneyCompact(it.stored_total)}` : ""} data-testid={`card-total-${it.id}`}>{moneyCompact(it.stored_total)}{it.standalone_total ? <span className="text-[9px] text-slate-400 ml-1">award · alone {moneyCompact(it.standalone_total)}</span> : null}</span>
                        ) : it.due_date ? (
                          <span className="text-[11px] text-slate-400">{fmtDate(it.due_date)}</span>
                        ) : null}
                        {it.team?.length ? <TeamAvatars assignee={it.assignee} team={it.team} testid={`card-team-${it.id}`} /> : <AssigneeAvatar assignee={it.assignee} testid={`card-assignee-${it.id}`} />}
                      </div>
                    </div>
                    {["lost", "no_bid"].includes(stageOf(it)) && it.loss_reason && <div className="text-[11px] text-rose-600 mt-1 line-clamp-1">Lost: {it.loss_reason}</div>}
                    {it.status === "cancelled" && it.cancel_reason && <div className="text-[11px] text-slate-500 mt-1 line-clamp-1">Cancelled: {it.cancel_reason}</div>}
                    {it.mobilization_date && (
                      <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500" data-testid={`card-timeline-${it.id}`}>
                        <CalendarClock className="w-3 h-3 text-[#003E87] shrink-0" />
                        <span>Mob {fmtDate(it.mobilization_date)}{it.schedule_project_end ? ` → ${fmtDate(it.schedule_project_end)}` : ""}</span>
                      </div>
                    )}
                    <NextAction signal={it.signal} testid={`card-action-${it.id}`} />
                    {it.kind === "bid" && it.external_priced && it.status === "sent" && canCancel && !selectMode && (
                      <QuickOutcome item={it} onChanged={load} onLost={(x) => setOutcomeBid({ ...x, initialMode: "lost" })} />
                    )}

                    {!selectMode && (
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                      {it.kind === "lead" && canCancel && it.status !== "lost" && (
                        <button title="Drop lead (records a reason)" data-testid={`pipeline-drop-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); setDropping(it); setDropReason(""); }}
                          className="text-slate-300 hover:text-amber-600"><XCircle className="w-3.5 h-3.5" /></button>
                      )}
                      {it.kind === "bid" && canCapture && !it.external_priced && ["estimating", "proposal"].includes(it.status) && (
                        <button title="Price elsewhere — record a lump sum and move to Sent" data-testid={`pipeline-price-elsewhere-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); setConvertBid(it); }}
                          className="text-slate-300 hover:text-indigo-600"><ExternalLink className="w-3.5 h-3.5" /></button>
                      )}
                      {it.kind === "bid" && canCancel && !["won", "lost", "cancelled", "declined"].includes(it.status) && (
                        <button title="Record outcome (won / lost / abandoned)" data-testid={`pipeline-outcome-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); setOutcomeBid(it); }}
                          className="text-slate-300 hover:text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                      )}
                      {it.kind === "bid" && canCancel && it.status !== "cancelled" && !["won", "lost"].includes(it.status) && (
                        <button title="Client lost the project — record as Lost" data-testid={`pipeline-cancel-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); setOutcomeBid({ ...it, initialMode: "cancelled" }); }}
                          className="text-slate-300 hover:text-amber-600"><XCircle className="w-3.5 h-3.5" /></button>
                      )}
                      {["lost", "no_bid"].includes(stageOf(it)) && canCancel && (
                        <button title="Reopen — bring this pursuit back into the pipeline" data-testid={`pipeline-reopen-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); reopenOne(it); }}
                          className="text-slate-300 hover:text-emerald-600"><RotateCcw className="w-3.5 h-3.5" /></button>
                      )}
                      {isAdmin && (
                        <button title="Delete permanently" data-testid={`pipeline-delete-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); setDeleting(it); }}
                          className="text-slate-300 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                    )}
                  </Card>
                ))}
                {col.items.length === 0 && <div className="text-xs text-slate-300 px-1 py-3 text-center">Empty</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fast capture */}
      <LeadCaptureDialog open={open} onOpenChange={setOpen} form={form} setForm={setForm} saving={saving} onSubmit={captureLead} />
      <ExternalBidDialog open={extOpen} onOpenChange={setExtOpen} onCreated={(b) => { setExtOpen(false); navigate(`/bids/${b.bid_id}`); }} />
      <ExternalBidDialog open={!!convertBid} convertBid={convertBid} onOpenChange={(o) => { if (!o) setConvertBid(null); }} onCreated={() => { setConvertBid(null); load(); }} />

      {/* Lead detail: edit → qualify → convert */}
      <LeadDetailDialog lead={lead} onClose={() => setLead(null)} leadEdit={leadEdit} setLeadEdit={setLeadEdit}
        gaps={gaps} onSave={saveLead} onConvert={convertLead} onDecide={openDecision} onActivityChange={loadFollowups} />

      {/* Drop lead */}
      <Dialog open={!!dropping} onOpenChange={(o) => { if (!o) setDropping(null); }}>
        <DialogContent data-testid="drop-lead-dialog">
          <DialogHeader><DialogTitle>Drop lead</DialogTitle>
            <DialogDescription>Records a reason and moves "{dropping?.name}" to Lost.</DialogDescription></DialogHeader>
          <div><Label className="text-xs">Reason</Label>
            <Textarea data-testid="drop-reason-input" value={dropReason} onChange={(e) => setDropReason(e.target.value)} rows={3} placeholder="e.g. Client shelved the project for a year" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDropping(null)}>Back</Button>
            <Button data-testid="drop-confirm-btn" onClick={confirmDrop} className="bg-amber-600 hover:bg-amber-700">Drop lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel bid */}
      <Dialog open={!!cancelling} onOpenChange={(o) => { if (!o) setCancelling(null); }}>
        <DialogContent data-testid="cancel-pursuit-dialog">
          <DialogHeader><DialogTitle>Cancel pursuit</DialogTitle>
            <DialogDescription>Recorded on the pursuit and in the activity log. Say why you are dropping "{cancelling?.name}".</DialogDescription></DialogHeader>
          <div><Label className="text-xs">Reason</Label>
            <Textarea data-testid="cancel-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(null)}>Back</Button>
            <Button data-testid="cancel-confirm-btn" onClick={confirmCancel} className="bg-amber-600 hover:bg-amber-700">Cancel pursuit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirm */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader><DialogTitle>Delete {selected.size} item{selected.size === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription>This permanently removes the selected leads and bids with no record kept. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Back</Button>
            <Button data-testid="bulk-delete-confirm" onClick={bulkDelete} className="bg-rose-600 hover:bg-rose-700">Delete {selected.size}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReassignDialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen} count={selected.size} onConfirm={bulkAssign} />
      <PipelineStageDialog open={bulkStageOpen} onOpenChange={setBulkStageOpen} count={selected.size} onConfirm={bulkStage} />
      <BulkScopeDialog open={bulkScopeOpen} onOpenChange={setBulkScopeOpen}
        items={[...selected].map((k) => { const [kind, id] = k.split(":"); return { kind, id }; })}
        onApply={bulkScope} />

      {/* Delete */}
      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <DialogContent data-testid="delete-pursuit-dialog">
          <DialogHeader><DialogTitle>Delete permanently</DialogTitle>
            <DialogDescription>This removes "{deleting?.name}" with no record kept. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Back</Button>
            <Button data-testid="delete-confirm-btn" onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BidDecisionDialog
        leadId={decisionLead?.lead_id}
        leadName={decisionLead?.project_name || decisionLead?.company || "lead"}
        open={decisionOpen}
        onOpenChange={setDecisionOpen}
        onDecided={() => { setDecisionOpen(false); setDecisionLead(null); load(); }}
      />

      <ConvertLeadDialog
        lead={convertLeadItem}
        open={!!convertLeadItem}
        onOpenChange={(o) => { if (!o) setConvertLeadItem(null); }}
        onConverted={(bidId) => { setConvertLeadItem(null); load(); navigate(`/bids/${bidId}`); }}
      />

      <OutcomeDialog
        bidId={outcomeBid?.id}
        bidName={outcomeBid?.name || "bid"}
        initialMode={outcomeBid?.initialMode}
        open={!!outcomeBid}
        onOpenChange={(o) => { if (!o) setOutcomeBid(null); }}
        onDone={() => { setOutcomeBid(null); load(); }}
      />
    </div>
  );
}
